/**
 * Roles & Responsibilities Registry Parser
 * 
 * Parses the consolidated Markdown registry file (content/roles-responsibilities.md)
 * and provides typed access to roles, RACI matrix, coverage model, and governance links.
 * 
 * This is the SINGLE SOURCE OF TRUTH for Roles & Responsibilities.
 */

import yaml from 'js-yaml';
import type {
  RolesRegistry,
  RoleRegistryEntry,
  RoleGroupDefinition,
  RoleStageResponsibility,
  RoleEscalationDuty,
  RoleToolAccess,
  RoleCoverageModel,
  GovernanceLink,
  RACIMatrix,
  RACIMatrixRow,
  RACIType,
  RoleGroup,
  PipelineBrand,
} from '../types';
import { getStageById, getAllStages } from './leadPipelineRegistry';
import { getChannelSLAById, getStageSLAById } from './slaCatalogueRegistry';

// Import the raw markdown content
import rolesMarkdown from '../../content/roles-responsibilities.md?raw';

// =============================================================================
// Parsing Utilities
// =============================================================================

/**
 * Parse YAML block from fenced code block
 */
function parseYamlBlock(content: string): Record<string, unknown> | null {
  const yamlMatch = content.match(/```yaml\n([\s\S]*?)\n```/);
  if (!yamlMatch) return null;
  
  try {
    return yaml.load(yamlMatch[1]) as Record<string, unknown>;
  } catch (e) {
    console.error('Failed to parse YAML block:', e);
    return null;
  }
}

/**
 * Extract section content by heading (## level)
 * Captures everything until the next ## heading (not ### subheading)
 */
function extractMainSection(content: string, heading: string): string {
  const startPattern = new RegExp(`## ${heading}`, 'i');
  const startMatch = content.match(startPattern);
  if (!startMatch || startMatch.index === undefined) return '';
  
  const startIdx = startMatch.index;
  const afterStart = content.substring(startIdx + startMatch[0].length);
  
  // Find the next ## heading that is NOT a ### subheading
  const nextSectionMatch = afterStart.match(/\n## (?!#)/);
  
  if (nextSectionMatch && nextSectionMatch.index !== undefined) {
    return content.substring(startIdx, startIdx + startMatch[0].length + nextSectionMatch.index);
  }
  
  return content.substring(startIdx);
}

// =============================================================================
// Document Info Parser
// =============================================================================

interface DocumentInfo {
  version: string;
  lastUpdated: string;
  description: string;
  descriptionAr: string;
}

function parseDocumentInfo(content: string): DocumentInfo {
  const section = extractMainSection(content, 'DOCUMENT INFO');
  const yamlData = parseYamlBlock(section);
  
  if (!yamlData || !yamlData.info) {
    console.warn('Roles Registry: Missing document info section');
    return {
      version: '1.0.0',
      lastUpdated: new Date().toISOString().split('T')[0],
      description: '',
      descriptionAr: '',
    };
  }
  
  const info = yamlData.info as Record<string, unknown>;
  
  return {
    version: String(info.version || '1.0.0'),
    lastUpdated: String(info.lastUpdated || new Date().toISOString().split('T')[0]),
    description: String(info.description || ''),
    descriptionAr: String(info.descriptionAr || ''),
  };
}

// =============================================================================
// Role Groups Parser
// =============================================================================

function parseRoleGroups(content: string): RoleGroupDefinition[] {
  const section = extractMainSection(content, 'ROLE GROUPS');
  const yamlData = parseYamlBlock(section);
  
  if (!yamlData || !yamlData.roleGroups) {
    console.warn('Roles Registry: Missing role groups section');
    return [];
  }
  
  const groupsRaw = yamlData.roleGroups as Array<Record<string, unknown>>;
  
  return groupsRaw.map(group => ({
    id: String(group.id || '') as RoleGroup,
    label: String(group.label || ''),
    labelAr: String(group.labelAr || ''),
    icon: group.icon ? String(group.icon) : undefined,
    color: group.color ? String(group.color) : undefined,
  })).filter(group => group.id);
}

// =============================================================================
// Roles Parser
// =============================================================================

function parseRoles(content: string): RoleRegistryEntry[] {
  const section = extractMainSection(content, 'ROLES');
  if (!section) {
    console.warn('Roles Registry: Missing ROLES section');
    return [];
  }
  
  const roles: RoleRegistryEntry[] = [];
  const validationErrors: string[] = [];
  
  // Find all role YAML blocks with meta
  const yamlBlockPattern = /```yaml\nmeta:\n([\s\S]*?)```/g;
  let match;
  
  while ((match = yamlBlockPattern.exec(section)) !== null) {
    try {
      const yamlContent = `meta:\n${match[1]}`;
      const yamlData = yaml.load(yamlContent) as Record<string, unknown>;
      
      if (!yamlData || !yamlData.meta) continue;
      
      const meta = yamlData.meta as Record<string, unknown>;
      const roleId = String(meta.id || '');
      
      if (!roleId) continue;
      
      // Check for duplicate IDs
      if (roles.some(r => r.id === roleId)) {
        validationErrors.push(`Duplicate role ID: ${roleId}`);
        continue;
      }
      
      // Parse responsibilities by stage with validation
      const responsibilitiesRaw = (yamlData.responsibilitiesByStage as Array<Record<string, unknown>>) || [];
      const responsibilities: RoleStageResponsibility[] = responsibilitiesRaw.map(resp => {
        const stageId = String(resp.stageId || '');
        
        // Validate stageId exists in pipeline registry
        if (stageId && stageId !== 'lead_capture') {
          const pipelineStage = getStageById(stageId);
          if (!pipelineStage) {
            validationErrors.push(`Role "${roleId}": Stage ID "${stageId}" not found in Lead Pipeline registry`);
          }
        }
        
        // Validate ownedSLAs references
        const ownedSLAs = (resp.ownedSLAs as string[]) || [];
        ownedSLAs.forEach(slaRef => {
          if (slaRef.startsWith('channel_')) {
            const channelSLA = getChannelSLAById(slaRef);
            if (!channelSLA) {
              validationErrors.push(`Role "${roleId}": Channel SLA "${slaRef}" not found in SLA Catalogue`);
            }
          } else if (slaRef.startsWith('stage_sla_')) {
            const stageSLA = getStageSLAById(slaRef);
            if (!stageSLA) {
              validationErrors.push(`Role "${roleId}": Stage SLA "${slaRef}" not found in SLA Catalogue`);
            }
          }
        });
        
        return {
          stageId,
          responsibilityType: (resp.responsibilityType as RACIType) || 'I',
          keyActions: (resp.keyActions as string[]) || [],
          keyActionsAr: (resp.keyActionsAr as string[]) || [],
          handoverInFrom: (resp.handoverInFrom as string[]) || [],
          handoverOutTo: (resp.handoverOutTo as string[]) || [],
          ownedKPIs: (resp.ownedKPIs as string[]) || [],
          ownedSLAs,
        };
      });
      
      // Parse escalation duties
      const escalationRaw = (yamlData.escalationDuties as Array<Record<string, unknown>>) || [];
      const escalationDuties: RoleEscalationDuty[] = escalationRaw.map(duty => ({
        breachType: String(duty.breachType || ''),
        breachTypeAr: String(duty.breachTypeAr || ''),
        action: String(duty.action || ''),
        actionAr: String(duty.actionAr || ''),
        timeframe: String(duty.timeframe || ''),
        timeframeAr: String(duty.timeframeAr || ''),
      }));
      
      // Parse tools access
      const toolsRaw = (yamlData.toolsAccess as Array<Record<string, unknown>>) || [];
      const toolsAccess: RoleToolAccess[] = toolsRaw.map(tool => ({
        tool: String(tool.tool || ''),
        toolAr: String(tool.toolAr || ''),
        accessLevel: (tool.accessLevel as 'full' | 'limited' | 'view_only') || 'view_only',
        description: tool.description ? String(tool.description) : undefined,
        descriptionAr: tool.descriptionAr ? String(tool.descriptionAr) : undefined,
      }));
      
      const role: RoleRegistryEntry = {
        id: roleId,
        title: String(meta.title || ''),
        titleAr: String(meta.titleAr || ''),
        group: (meta.group as RoleGroup) || 'sales',
        department: String(meta.department || ''),
        departmentAr: String(meta.departmentAr || ''),
        reportsTo: String(meta.reportsTo || ''),
        reportsToAr: String(meta.reportsToAr || ''),
        purpose: String(meta.purpose || ''),
        purposeAr: String(meta.purposeAr || ''),
        scope: String(meta.scope || ''),
        scopeAr: String(meta.scopeAr || ''),
        appliesToBrands: (meta.appliesToBrands as PipelineBrand[]) || ['Changan', 'Hyundai', 'Stellantis'],
        responsibilitiesByStage: responsibilities,
        escalationDuties,
        toolsAccess,
        templatesUsed: (yamlData.templatesUsed as string[]) || [],
        templatesUsedAr: (yamlData.templatesUsedAr as string[]) || [],
        qualityChecklist: (yamlData.qualityChecklist as string[]) || [],
        qualityChecklistAr: (yamlData.qualityChecklistAr as string[]) || [],
        trainingRequired: (yamlData.trainingRequired as string[]) || [],
        trainingRequiredAr: (yamlData.trainingRequiredAr as string[]) || [],
        icon: meta.icon ? String(meta.icon) : undefined,
        color: meta.color ? String(meta.color) : undefined,
      };
      
      roles.push(role);
    } catch (e) {
      console.error('Failed to parse role:', e);
    }
  }
  
  // Log validation errors
  if (validationErrors.length > 0) {
    console.warn('Roles Registry Validation Warnings:');
    validationErrors.forEach(err => console.warn(`  - ${err}`));
  }
  
  return roles;
}

// =============================================================================
// Coverage Model Parser
// =============================================================================

function parseCoverageModel(content: string): RoleCoverageModel {
  const section = extractMainSection(content, 'COVERAGE MODEL');
  const yamlData = parseYamlBlock(section);
  
  if (!yamlData || !yamlData.coverageModel) {
    console.warn('Roles Registry: Missing coverage model section');
    return {
      rolesPerBranch: [],
      minimumStaffing: '',
      minimumStaffingAr: '',
      backupRules: [],
      backupRulesAr: [],
    };
  }
  
  const model = yamlData.coverageModel as Record<string, unknown>;
  const rolesPerBranchRaw = (model.rolesPerBranch as Array<Record<string, unknown>>) || [];
  
  return {
    rolesPerBranch: rolesPerBranchRaw.map(r => ({
      roleId: String(r.roleId || ''),
      minimumCount: Number(r.minimumCount) || 0,
      recommendedCount: Number(r.recommendedCount) || 0,
    })),
    minimumStaffing: String(model.minimumStaffing || ''),
    minimumStaffingAr: String(model.minimumStaffingAr || ''),
    backupRules: (model.backupRules as string[]) || [],
    backupRulesAr: (model.backupRulesAr as string[]) || [],
  };
}

// =============================================================================
// Governance Links Parser
// =============================================================================

function parseGovernanceLinks(content: string): GovernanceLink[] {
  const section = extractMainSection(content, 'GOVERNANCE LINKS');
  const yamlData = parseYamlBlock(section);
  
  if (!yamlData || !yamlData.governanceLinks) {
    return [];
  }
  
  const linksRaw = yamlData.governanceLinks as Array<Record<string, unknown>>;
  
  return linksRaw.map(link => ({
    module: String(link.module || ''),
    moduleAr: String(link.moduleAr || ''),
    description: String(link.description || ''),
    descriptionAr: String(link.descriptionAr || ''),
    path: link.path ? String(link.path) : undefined,
  }));
}

// =============================================================================
// Main Parser Function
// =============================================================================

/**
 * Parse the complete Roles Registry
 */
function parseRolesRegistry(markdown: string): RolesRegistry {
  const docInfo = parseDocumentInfo(markdown);
  const roleGroups = parseRoleGroups(markdown);
  const roles = parseRoles(markdown);
  const coverageModel = parseCoverageModel(markdown);
  const governanceLinks = parseGovernanceLinks(markdown);
  
  return {
    version: docInfo.version,
    lastUpdated: docInfo.lastUpdated,
    description: docInfo.description,
    descriptionAr: docInfo.descriptionAr,
    roleGroups,
    roles,
    coverageModel,
    governanceLinks,
  };
}

// =============================================================================
// Cached Registry & Export Functions
// =============================================================================

let cachedRegistry: RolesRegistry | null = null;

/**
 * Get the parsed Roles Registry
 */
export function getRolesRegistry(): RolesRegistry {
  if (!cachedRegistry) {
    cachedRegistry = parseRolesRegistry(rolesMarkdown);
  }
  return cachedRegistry;
}

/**
 * Get all role groups
 */
export function getRoleGroups(): RoleGroupDefinition[] {
  return getRolesRegistry().roleGroups;
}

/**
 * Get a role group by ID
 */
export function getRoleGroupById(groupId: RoleGroup): RoleGroupDefinition | undefined {
  return getRoleGroups().find(g => g.id === groupId);
}

/**
 * Get all roles
 */
export function getAllRoles(): RoleRegistryEntry[] {
  return getRolesRegistry().roles;
}

/**
 * Get a role by ID
 */
export function getRoleById(roleId: string): RoleRegistryEntry | undefined {
  return getAllRoles().find(r => r.id === roleId);
}

/**
 * List roles with optional filters
 */
export function listRoles(filters?: {
  brand?: PipelineBrand;
  group?: RoleGroup;
  stageId?: string;
  search?: string;
}): RoleRegistryEntry[] {
  let roles = getAllRoles();
  
  if (filters?.brand) {
    roles = roles.filter(r => r.appliesToBrands.includes(filters.brand!));
  }
  
  if (filters?.group) {
    roles = roles.filter(r => r.group === filters.group);
  }
  
  if (filters?.stageId) {
    roles = roles.filter(r => 
      r.responsibilitiesByStage.some(resp => resp.stageId === filters.stageId)
    );
  }
  
  if (filters?.search) {
    const searchLower = filters.search.toLowerCase();
    roles = roles.filter(r => 
      r.title.toLowerCase().includes(searchLower) ||
      r.titleAr.includes(filters.search!) ||
      r.purpose.toLowerCase().includes(searchLower) ||
      r.department.toLowerCase().includes(searchLower)
    );
  }
  
  return roles;
}

/**
 * Get roles that have responsibilities for a specific stage
 */
export function getRolesForStage(stageId: string): RoleRegistryEntry[] {
  return getAllRoles().filter(role => 
    role.responsibilitiesByStage.some(resp => resp.stageId === stageId)
  );
}

/**
 * Get RACI entries for a specific stage
 */
export function getRACIForStage(stageId: string): { roleId: string; roleTitle: string; roleTitleAr: string; value: RACIType }[] {
  const rolesWithStage = getRolesForStage(stageId);
  
  return rolesWithStage.map(role => {
    const resp = role.responsibilitiesByStage.find(r => r.stageId === stageId);
    return {
      roleId: role.id,
      roleTitle: role.title,
      roleTitleAr: role.titleAr,
      value: resp?.responsibilityType || 'I',
    };
  }).sort((a, b) => {
    // Sort by RACI priority: R > A > C > I
    const order = { R: 0, A: 1, C: 2, I: 3 };
    return order[a.value] - order[b.value];
  });
}

/**
 * Compute the complete RACI Matrix
 * Derives from role responsibilities - no duplication of data
 */
export function computeRACIMatrix(): RACIMatrix {
  const roles = getAllRoles();
  const stages = getAllStages();
  
  // Get unique role list for columns
  const roleColumns = roles.map(r => ({
    id: r.id,
    title: r.title,
    titleAr: r.titleAr,
    group: r.group,
  }));
  
  // Build rows from stages
  const rows: RACIMatrixRow[] = stages.map(stage => {
    const cells = roles.map(role => {
      const resp = role.responsibilitiesByStage.find(r => r.stageId === stage.meta.id);
      return {
        roleId: role.id,
        value: resp?.responsibilityType || null,
      };
    });
    
    return {
      stageId: stage.meta.id,
      stageName: stage.meta.title,
      stageNameAr: stage.meta.titleAr,
      stageOrder: stage.meta.order,
      cells,
    };
  });
  
  return {
    roles: roleColumns,
    rows: rows.sort((a, b) => a.stageOrder - b.stageOrder),
  };
}

/**
 * Get coverage model
 */
export function getCoverageModel(): RoleCoverageModel {
  return getRolesRegistry().coverageModel;
}

/**
 * Get governance links
 */
export function getGovernanceLinks(): GovernanceLink[] {
  return getRolesRegistry().governanceLinks;
}

/**
 * Get role counts for summary
 */
export function getRoleCounts(): {
  totalRoles: number;
  byGroup: { group: RoleGroup; count: number }[];
} {
  const roles = getAllRoles();
  const groups = getRoleGroups();
  
  const byGroup = groups.map(g => ({
    group: g.id,
    count: roles.filter(r => r.group === g.id).length,
  })).filter(g => g.count > 0);
  
  return {
    totalRoles: roles.length,
    byGroup,
  };
}

/**
 * Validate role references exist
 * Returns array of validation errors (empty if all valid)
 */
export function validateRoleReferences(): string[] {
  const errors: string[] = [];
  const roles = getAllRoles();
  const roleIds = new Set(roles.map(r => r.id));
  
  roles.forEach(role => {
    role.responsibilitiesByStage.forEach(resp => {
      // Validate handover references
      resp.handoverInFrom?.forEach(fromId => {
        if (fromId && !roleIds.has(fromId)) {
          errors.push(`Role "${role.id}": handoverInFrom references unknown role "${fromId}"`);
        }
      });
      
      resp.handoverOutTo?.forEach(toId => {
        if (toId && !roleIds.has(toId)) {
          errors.push(`Role "${role.id}": handoverOutTo references unknown role "${toId}"`);
        }
      });
    });
  });
  
  return errors;
}

/**
 * Export the raw registry for debugging
 */
export const registry = getRolesRegistry();

export default registry;
